/**
 * Staff "My Learning" — classroom courses a staff member can take (TRUS-225).
 * Lists published staff-audience courses for the active org; opens the learner
 * viewer. Staff auth (authFetch); enrollment is keyed on the staff user_id.
 */
import { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { BookOpen, Play, ArrowLeft } from 'lucide-react';
import { getWeaveinApiUrl } from '@/src/config';
import { authFetch } from '@/lib/authFetch';
import { PageHeader, PageContainer, PageState } from '@/components/weavein';

interface Course {
  id: number;
  title: string;
  description: string | null;
  module_count: number;
  lesson_count: number;
}

const activeCboId = () => localStorage.getItem('active_cbo_id') || '';

export default function StaffLearnPage() {
  const navigate = useNavigate();
  const [courses, setCourses] = useState<Course[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const cbo = activeCboId();

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await authFetch(getWeaveinApiUrl(`/api/lms/staff/courses?cbo_id=${cbo}`));
      if (!res.ok) throw new Error('Failed to load courses');
      setCourses(await res.json());
    } catch (err: any) {
      setError(err.message || 'Failed to load courses');
    } finally {
      setLoading(false);
    }
  }, [cbo]);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <div className="flex h-full flex-col">
      <PageHeader
        eyebrow="Workforce · Learning"
        title="My Learning"
        description="Training courses assigned to your team."
      />
      <PageContainer>
        {error && (
          <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-red-700 dark:text-red-300 text-sm">
            {error}
          </div>
        )}

        {loading ? (
          <PageState variant="loading" title="Loading…" />
        ) : courses.length === 0 ? (
          <PageState variant="empty" title="No courses available yet." />
        ) : (
          <div className="grid gap-4">
            {courses.map((course) => (
              <Card
                key={course.id}
                className="hover:shadow-md transition-shadow cursor-pointer"
                onClick={() => navigate(`/workforce/learn/${course.id}`)}
              >
                <CardHeader className="pb-2">
                  <div className="flex items-start justify-between gap-2">
                    <CardTitle className="text-base">{course.title}</CardTitle>
                    <Badge variant="secondary" className="shrink-0 text-xs">
                      {course.lesson_count} lesson{course.lesson_count !== 1 ? 's' : ''}
                    </Badge>
                  </div>
                  {course.description && (
                    <CardDescription className="text-sm line-clamp-2">{course.description}</CardDescription>
                  )}
                </CardHeader>
                <CardContent className="pt-0">
                  <div className="flex items-center justify-between">
                    <span className="flex items-center gap-1 text-xs text-ink-500">
                      <BookOpen className="w-3 h-3" /> {course.module_count} module{course.module_count !== 1 ? 's' : ''}
                    </span>
                    <Button size="sm" className="gap-1 text-xs h-7">
                      <Play className="w-3 h-3" /> Start
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        <div className="mt-8">
          <Link to="/workforce/curricula">
            <Button variant="ghost" size="sm" className="gap-1 text-ink-500">
              <ArrowLeft className="w-4 h-4" /> Back to Curricula
            </Button>
          </Link>
        </div>
      </PageContainer>
    </div>
  );
}
